import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { Criterion, MatrixFilter, Position } from "@/types";
import { FilterEditor } from "@/components/shared/common/FilterEditor";

const emptyCriterion: Criterion = { name: "", weight: 10, requirement: "" };

const defaultCriteria: Criterion[] = [
  { name: "Years of experience", weight: 25, requirement: "3+ years" },
  { name: "Required skills", weight: 40, requirement: "List required skills" },
  { name: "Education", weight: 15, requirement: "BS or equivalent" },
  { name: "Communication", weight: 20, requirement: "Clear written + verbal" },
];

interface NewMatrixFormProps {
  positions: Position[];
  onPositionsChange: () => void;
  onCreated: () => void;
  onCancel: () => void;
}

export function NewMatrixForm({ positions, onPositionsChange, onCreated, onCancel }: NewMatrixFormProps) {
  const [positionId, setPositionId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [employmentType, setEmploymentType] = useState<"regular" | "contractual" | "intern">("regular");
  const [criteria, setCriteria] = useState<Criterion[]>(defaultCriteria);
  const [filters, setFilters] = useState<MatrixFilter[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showNewPosition, setShowNewPosition] = useState(false);
  const [newPosTitle, setNewPosTitle] = useState("");
  const [newPosDesc, setNewPosDesc] = useState("");

  const totalWeight = criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);

  useEffect(() => {
    if (positionId == null && positions.length > 0) setPositionId(positions[0].id);
  }, [positions, positionId]);

  const createPosition = async () => {
    if (!newPosTitle.trim()) return;
    try {
      const { position } = await api.createPosition({ title: newPosTitle.trim(), description: newPosDesc.trim() || undefined });
      setNewPosTitle("");
      setNewPosDesc("");
      setShowNewPosition(false);
      setPositionId(position.id);
      onPositionsChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const submit = async () => {
    setError(null);
    if (!positionId) return setError("Select a position first");
    if (!name.trim()) return setError("Matrix name is required");
    if (criteria.some((c) => !c.name.trim() || !c.requirement.trim())) return setError("Each criterion needs a name and a requirement");
    if (totalWeight <= 0) return setError("Weights must sum to > 0");

    setSaving(true);
    try {
      await api.createMatrix({ position_id: positionId, name: name.trim(), employment_type: employmentType, criteria, filters });
      setName("");
      setEmploymentType("regular");
      setCriteria([...defaultCriteria]);
      setFilters([]);
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-card bg-white p-5 shadow-card">
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-text-secondary">Position</label>
            <button className="text-xs font-medium text-rausch hover:underline" onClick={() => setShowNewPosition(!showNewPosition)}>
              {showNewPosition ? "Cancel" : "+ New position"}
            </button>
          </div>
          {showNewPosition ? (
            <div className="mt-2 space-y-2 rounded-badge bg-surface-secondary p-3">
              <input className="w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus" placeholder="Position title" value={newPosTitle} onChange={(e) => setNewPosTitle(e.target.value)} />
              <input className="w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus" placeholder="Description (optional)" value={newPosDesc} onChange={(e) => setNewPosDesc(e.target.value)} />
              <button className="rounded-btn bg-near-black px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-rausch" onClick={createPosition}>Create position</button>
            </div>
          ) : (
            <select className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus" value={positionId ?? ""} onChange={(e) => setPositionId(Number(e.target.value) || null)}>
              {positions.length === 0 && <option value="">— create a position first —</option>}
              {positions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-text-secondary">Matrix name</label>
          <input className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Technical Screen" />
        </div>

        <div>
          <label className="text-xs font-semibold text-text-secondary">Employment type</label>
          <div className="mt-1 flex gap-2">
            {(["regular", "contractual", "intern"] as const).map((t) => (
              <button key={t} type="button" onClick={() => setEmploymentType(t)} className={`rounded-btn px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${employmentType === t ? "bg-near-black text-white" : "bg-surface-secondary text-text-secondary hover:bg-near-black hover:text-white"}`}>
                {t}
              </button>
            ))}
          </div>
          {employmentType === "intern" && (
            <p className="mt-1 text-[11px] text-amber-700">Intern mode: candidates currently pursuing their degree will meet education requirements.</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-text-secondary">Criteria</label>
            <span className="text-xs font-medium text-text-secondary">Total weight: {totalWeight}</span>
          </div>
          <div className="mt-2 space-y-2">
            {criteria.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 rounded-btn border border-border-card bg-surface-secondary p-2">
                <input className="col-span-3 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus" placeholder="Criterion" value={c.name} onChange={(e) => setCriteria(criteria.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                <input type="number" min={0} className="col-span-2 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus" placeholder="Weight" value={c.weight} onChange={(e) => setCriteria(criteria.map((x, j) => j === i ? { ...x, weight: Number(e.target.value) } : x))} />
                <input className="col-span-6 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus" placeholder="Requirement" value={c.requirement} onChange={(e) => setCriteria(criteria.map((x, j) => j === i ? { ...x, requirement: e.target.value } : x))} />
                <button className="col-span-1 rounded-full text-sm text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text" onClick={() => setCriteria(criteria.filter((_, j) => j !== i))} title="Remove">✕</button>
              </div>
            ))}
          </div>
          <button className="mt-2 text-sm font-medium text-rausch hover:underline" onClick={() => setCriteria([...criteria, { ...emptyCriterion }])}>+ Add criterion</button>
        </div>

        <div>
          <label className="text-xs font-semibold text-text-secondary">Filter requirements (auto-reject)</label>
          <div className="mt-2">
            <FilterEditor filters={filters} onChange={setFilters} />
          </div>
        </div>

        {error && <p className="text-sm font-medium text-error-text">{error}</p>}

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:text-near-black">Cancel</button>
          <button disabled={saving} className="rounded-btn bg-near-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:bg-border-card disabled:text-text-secondary" onClick={submit}>
            {saving ? "Saving…" : "Save matrix"}
          </button>
        </div>
      </div>
    </div>
  );
}
